import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { GraduationCap, Loader2 } from "lucide-react";

export default function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      const dest = res.user.role === "admin" ? "/admin" : res.user.role === "principal" ? "/billing" : "/dashboard";
      navigate(dest);
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto flex max-w-md flex-col px-5 py-14">
        <div className="mb-8 text-center">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-3xl bg-primary text-primary-foreground">
            <GraduationCap className="h-7 w-7" strokeWidth={1.75} />
          </span>
          <h1 className="mt-5 font-heading text-3xl font-semibold text-foreground">{t("login_title")}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t("login_subtitle")}</p>
        </div>

        <form onSubmit={submit} className="space-y-4 rounded-3xl border border-black/5 bg-card p-7 shadow-sm">
          {error && (
            <div data-testid="login-error" className="rounded-2xl bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
              {error}
            </div>
          )}
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground">{t("field_email")}</label>
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground">{t("field_password")}</label>
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <Button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="h-12 w-full rounded-full bg-primary text-base font-semibold text-primary-foreground hover:bg-primary-hover"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("btn_login")}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t("no_account")}{" "}
          <Link data-testid="go-register" to="/register" className="font-semibold text-primary hover:underline">
            {t("nav_signup")}
          </Link>
        </p>
      </div>
    </div>
  );
}
